import { CHURN_RISK_LABELS } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  PLAY_KIND_LABELS,
  PLAYBOOK_CHAPTER_ORDER,
  PLAYBOOK_SORTS,
  type PlaybookChapterKey,
  type PlaybookSort,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"
import { formatDeadline } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import {
  isUrgentPlayGroup,
  type PortfolioPlay,
  type PortfolioPlayGroup,
} from "@/v2-backoffice-client/portfolio/portfolio.plays.utils"

export function playbookSortLabel(sort: PlaybookSort) {
  return PLAYBOOK_SORTS.find((option) => option.key === sort)?.label ?? ""
}

/** The account groups in the chosen order; urgency keeps the derived order. */
export function sortPlayGroups(
  groups: PortfolioPlayGroup[],
  sort: PlaybookSort,
) {
  if (sort === "urgency") return groups
  const sorted = [...groups]
  switch (sort) {
    case "renewal":
      return sorted.sort(
        (a, b) =>
          (a.deadline?.daysUntil ?? Number.MAX_SAFE_INTEGER) -
          (b.deadline?.daysUntil ?? Number.MAX_SAFE_INTEGER),
      )
    case "value":
      // Biggest 12-month books first; accounts without invoice history last.
      return sorted.sort((a, b) => (b.billed12M ?? -1) - (a.billed12M ?? -1))
    case "seats":
      return sorted.sort((a, b) => b.seatCount - a.seatCount)
    case "name":
      return sorted.sort((a, b) => a.customerName.localeCompare(b.customerName))
  }
}

/** Distinct accounts behind a list of plays. */
export function countPlayAccounts(plays: PortfolioPlay[]) {
  return new Set(plays.map((play) => play.customerExternalId)).size
}

export type PlaybookChapter = {
  /** null = one flat list (an explicit sort order, not urgency). */
  key: PlaybookChapterKey | null
  groups: PortfolioPlayGroup[]
}

/** The chapter an account sits in: its most urgent tone, actions by urgency. */
export function playbookChapterOf(
  group: PortfolioPlayGroup,
): PlaybookChapterKey {
  if (group.tone === "action") {
    return isUrgentPlayGroup(group) ? "act-now" : "watch"
  }
  return group.tone
}

/**
 * In urgency order the list reads as chapters (act now, keep an eye on,
 * openings, wins), each account under its most urgent tone. Under an
 * explicit sort the chapters would break the order the reader asked for,
 * so it is one list.
 */
export function chapterPlayGroups(
  groups: PortfolioPlayGroup[],
  sort: PlaybookSort,
): PlaybookChapter[] {
  if (sort !== "urgency") return [{ key: null, groups }]
  return PLAYBOOK_CHAPTER_ORDER.map((key) => ({
    key,
    groups: groups.filter((group) => playbookChapterOf(group) === key),
  })).filter((chapter) => chapter.groups.length > 0)
}

/**
 * An account's plays as plain text — the HubSpot note, so nobody retypes
 * them: a header line with the standing, then one line per play.
 */
export function formatPlaybookNote(group: PortfolioPlayGroup) {
  const standing = [
    CHURN_RISK_LABELS[group.churnLevel],
    group.billed12M != null && group.billed12M > 0
      ? `${formatCompactAmount(group.billed12M, group.primaryCurrency)} billed 12 mo`
      : null,
    group.deadline ? formatDeadline(group.deadline) : null,
  ].filter((part): part is string => part != null)
  return [
    `${group.customerName} — ${standing.join(" · ")}`,
    ...group.plays.map(
      (play) => `• ${PLAY_KIND_LABELS[play.kind]}: ${play.text}`,
    ),
  ].join("\n")
}
